export const PUBLICATION_AI_ANALYSIS_SCHEMA_VERSION =
  'publication-ai-analysis-v1' as const;

export type PublicationInboxReviewDecision =
  | 'accepted'
  | 'corrected'
  | 'rejected';

export type PublicationInboxActionRequirement =
  | 'yes'
  | 'no'
  | 'uncertain';

export type PublicationInboxActionType =
  | 'petition'
  | 'review'
  | 'hearing'
  | 'payment'
  | 'information'
  | 'appeal'
  | 'other'
  | 'none';

export type PublicationInboxDeadlineSource =
  | 'explicit'
  | 'inferred'
  | 'unknown';

export type PublicationInboxDeadlineUnit =
  | 'days'
  | 'hours';

export type PublicationInboxDeadlineCountType =
  | 'business_days'
  | 'calendar_days'
  | 'unspecified';

export type PublicationInboxReviewCandidate = {
  schemaVersion:
    typeof PUBLICATION_AI_ANALYSIS_SCHEMA_VERSION;
  summary: {
    title: string;
    concise: string;
    context: string | null;
  };
  classification: {
    communication:
      | 'intimation'
      | 'citation'
      | 'notification'
      | 'unknown';
    subject:
      | 'expert_evidence'
      | 'hearing'
      | 'payment'
      | 'appellate_decision'
      | 'judgment'
      | 'decision'
      | 'procedural_movement'
      | 'administrative'
      | 'other';
    event:
      | 'manifest_on_expert_report'
      | 'present_defense'
      | 'present_counterarguments'
      | 'amend_initial'
      | 'pay_costs'
      | 'attend_hearing'
      | 'provide_information'
      | 'comply_order'
      | 'withdraw_values'
      | 'appeal'
      | 'take_notice_decision'
      | 'no_action'
      | 'other';
    priority:
      | 'low'
      | 'medium'
      | 'high'
      | 'uncertain';
  };
  action: {
    requiresAction:
      PublicationInboxActionRequirement;
    actionType:
      PublicationInboxActionType;
    suggestedAction: string | null;
    affectedParty: string | null;
  };
  deadline: {
    source:
      PublicationInboxDeadlineSource;
    value: number | null;
    unit:
      PublicationInboxDeadlineUnit | null;
    countType:
      PublicationInboxDeadlineCountType | null;
    legalBasis: string | null;
    requiresConfirmation: true;
  };
  evidence: Array<{
    id: string;
    supports:
      | 'summary'
      | 'classification'
      | 'action'
      | 'deadline';
    sourceField:
      | 'title'
      | 'content';
    quote: string;
  }>;
  warnings: Array<{
    code: string;
    message: string;
  }>;
  confidence: {
    overall:
      | 'high'
      | 'medium'
      | 'low';
    action:
      | 'high'
      | 'medium'
      | 'low';
    deadline:
      | 'high'
      | 'medium'
      | 'low';
  };
};

export type PublicationInboxCorrectionDraft = {
  summaryTitle: string;
  summaryConcise: string;
  summaryContext: string;
  communication:
    PublicationInboxReviewCandidate['classification']['communication'];
  subject:
    PublicationInboxReviewCandidate['classification']['subject'];
  event:
    PublicationInboxReviewCandidate['classification']['event'];
  priority:
    PublicationInboxReviewCandidate['classification']['priority'];
  requiresAction:
    PublicationInboxActionRequirement;
  actionType:
    PublicationInboxActionType;
  suggestedAction: string;
  affectedParty: string;
  deadlineSource:
    PublicationInboxDeadlineSource;
  deadlineValue: string;
  deadlineUnit:
    PublicationInboxDeadlineUnit | '';
  deadlineCountType:
    PublicationInboxDeadlineCountType | '';
  legalBasis: string;
  deadlineEvidenceQuote: string;
};

const isRecord = (
  value: unknown
): value is Record<string, unknown> =>
  Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value)
  );

export const parsePublicationInboxReviewCandidate = (
  value: unknown
): PublicationInboxReviewCandidate => {
  if (
    !isRecord(value) ||
    value.schemaVersion !==
      PUBLICATION_AI_ANALYSIS_SCHEMA_VERSION ||
    !isRecord(value.summary) ||
    !isRecord(value.classification) ||
    !isRecord(value.action) ||
    !isRecord(value.deadline) ||
    !Array.isArray(value.evidence) ||
    !Array.isArray(value.warnings) ||
    !isRecord(value.confidence)
  ) {
    throw new Error(
      'A análise estruturada disponível não possui o contrato esperado.'
    );
  }

  return value as PublicationInboxReviewCandidate;
};

const optionalText = (
  value: unknown
) => {
  const result = String(
    value ?? ''
  ).trim();

  return result || null;
};

export const publicationInboxCandidateToDraft = (
  candidate: PublicationInboxReviewCandidate
): PublicationInboxCorrectionDraft => {
  const deadlineEvidence =
    candidate.evidence.find(
      (item) =>
        item.supports === 'deadline'
    );

  return {
    summaryTitle:
      candidate.summary.title,
    summaryConcise:
      candidate.summary.concise,
    summaryContext:
      candidate.summary.context ?? '',
    communication:
      candidate.classification
        .communication,
    subject:
      candidate.classification.subject,
    event:
      candidate.classification.event,
    priority:
      candidate.classification.priority,
    requiresAction:
      candidate.action.requiresAction,
    actionType:
      candidate.action.actionType,
    suggestedAction:
      candidate.action.suggestedAction ?? '',
    affectedParty:
      candidate.action.affectedParty ?? '',
    deadlineSource:
      candidate.deadline.source,
    deadlineValue:
      candidate.deadline.value === null
        ? ''
        : String(candidate.deadline.value),
    deadlineUnit:
      candidate.deadline.unit ?? '',
    deadlineCountType:
      candidate.deadline.countType ?? '',
    legalBasis:
      candidate.deadline.legalBasis ?? '',
    deadlineEvidenceQuote:
      deadlineEvidence?.quote ?? '',
  };
};

const positiveDeadlineValue = (
  value: string
) => {
  const parsed = Number(value);

  if (
    !Number.isInteger(parsed) ||
    parsed <= 0 ||
    parsed > 8760
  ) {
    throw new Error(
      'Informe um prazo inteiro positivo entre 1 e 8760.'
    );
  }

  return parsed;
};

const replaceDeadlineEvidence = (
  candidate:
    PublicationInboxReviewCandidate,
  quote: string,
  required: boolean
) => {
  const evidence =
    candidate.evidence.map(
      (item) => ({ ...item })
    );

  const normalizedQuote =
    quote.trim();

  const index =
    evidence.findIndex(
      (item) =>
        item.supports === 'deadline'
    );

  if (
    required &&
    !normalizedQuote
  ) {
    throw new Error(
      'Informe o trecho da publicação que fundamenta o prazo.'
    );
  }

  if (
    index >= 0 &&
    normalizedQuote
  ) {
    evidence[index] = {
      ...evidence[index],
      quote:
        normalizedQuote.slice(0, 600),
    };
  } else if (
    index < 0 &&
    normalizedQuote
  ) {
    evidence.push({
      id:
        'human-deadline-review',
      supports: 'deadline',
      sourceField: 'content',
      quote:
        normalizedQuote.slice(0, 600),
    });
  }

  return evidence;
};

export const buildPublicationInboxCorrectedAnalysis = (
  candidate:
    PublicationInboxReviewCandidate,
  draft:
    PublicationInboxCorrectionDraft
): PublicationInboxReviewCandidate => {
  const summaryTitle =
    draft.summaryTitle.trim();

  const summaryConcise =
    draft.summaryConcise.trim();

  if (!summaryTitle) {
    throw new Error(
      'O título jurídico da revisão é obrigatório.'
    );
  }

  if (!summaryConcise) {
    throw new Error(
      'O resumo jurídico da revisão é obrigatório.'
    );
  }

  let actionType =
    draft.actionType;

  let suggestedAction =
    optionalText(
      draft.suggestedAction
    );

  if (
    draft.requiresAction === 'no'
  ) {
    actionType = 'none';
    suggestedAction = null;
  }

  if (
    draft.requiresAction === 'yes'
  ) {
    if (actionType === 'none') {
      throw new Error(
        'Selecione o tipo da providência obrigatória.'
      );
    }

    if (!suggestedAction) {
      throw new Error(
        'Descreva a providência quando a publicação exige ação.'
      );
    }
  }

  let deadlineValue:
    number | null = null;

  let deadlineUnit:
    PublicationInboxDeadlineUnit | null =
    null;

  let deadlineCountType:
    PublicationInboxDeadlineCountType | null =
    null;

  let legalBasis:
    string | null = null;

  if (
    draft.deadlineSource !==
    'unknown'
  ) {
    deadlineValue =
      positiveDeadlineValue(
        draft.deadlineValue
      );

    if (!draft.deadlineUnit) {
      throw new Error(
        'Selecione a unidade do prazo.'
      );
    }

    if (!draft.deadlineCountType) {
      throw new Error(
        'Selecione o tipo de contagem do prazo.'
      );
    }

    deadlineUnit =
      draft.deadlineUnit;

    deadlineCountType =
      draft.deadlineCountType;

    legalBasis =
      optionalText(
        draft.legalBasis
      );

    if (
      draft.deadlineSource ===
        'inferred' &&
      !legalBasis
    ) {
      throw new Error(
        'Prazo inferido exige fundamento jurídico.'
      );
    }
  }

  const evidence =
    replaceDeadlineEvidence(
      candidate,
      draft.deadlineEvidenceQuote,
      draft.deadlineSource !==
        'unknown'
    );

  return {
    schemaVersion:
      PUBLICATION_AI_ANALYSIS_SCHEMA_VERSION,
    summary: {
      title:
        summaryTitle.slice(0, 180),
      concise:
        summaryConcise.slice(0, 900),
      context:
        optionalText(
          draft.summaryContext
        ),
    },
    classification: {
      communication:
        draft.communication,
      subject:
        draft.subject,
      event:
        draft.event,
      priority:
        draft.priority,
    },
    action: {
      requiresAction:
        draft.requiresAction,
      actionType,
      suggestedAction,
      affectedParty:
        optionalText(
          draft.affectedParty
        ),
    },
    deadline: {
      source:
        draft.deadlineSource,
      value:
        deadlineValue,
      unit:
        deadlineUnit,
      countType:
        deadlineCountType,
      legalBasis,
      requiresConfirmation:
        true,
    },
    evidence,
    warnings:
      candidate.warnings.map(
        (item) => ({ ...item })
      ),
    confidence: {
      ...candidate.confidence,
    },
  };
};

const publicationInboxCanonicalValue = (
  value: unknown
): unknown => {
  if (Array.isArray(value)) {
    return value.map(
      publicationInboxCanonicalValue
    );
  }

  if (
    value &&
    typeof value === 'object'
  ) {
    const record =
      value as Record<
        string,
        unknown
      >;

    return Object.fromEntries(
      Object.keys(record)
        .sort()
        .map(
          (key) => [
            key,
            publicationInboxCanonicalValue(
              record[key]
            ),
          ]
        )
    );
  }

  return value;
};

export const publicationInboxReviewCandidateEquals = (
  left:
    PublicationInboxReviewCandidate,
  right:
    PublicationInboxReviewCandidate
) =>
  JSON.stringify(
    publicationInboxCanonicalValue(
      left
    )
  ) ===
  JSON.stringify(
    publicationInboxCanonicalValue(
      right
    )
  );

export const publicationInboxReviewResultMessage = (
  decision:
    PublicationInboxReviewDecision,
  operationalizationState:
    string | null
) => {
  switch (
    operationalizationState
  ) {
    case 'deadline_pending_confirmation':
      return 'Revisão registrada. A data final do possível prazo ainda precisa ser confirmada pelo advogado.';
    case 'task_created':
      return 'Revisão registrada e providência operacional criada.';
    case 'no_action':
      return 'Revisão registrada sem providência operacional.';
    case 'review_required':
      return 'Revisão registrada, mas a providência jurídica permanece dependente de decisão humana.';
    case 'rejected':
      return 'Análise rejeitada. A publicação permanece ativa para decisão manual.';
    case 'exception':
      return 'A revisão foi registrada, mas a operacionalização encontrou uma pendência.';
    default:
      return decision === 'rejected'
        ? 'Análise rejeitada. A publicação permanece ativa.'
        : 'Revisão humana registrada.';
  }
};